import { amenitiesInfo } from "@/info/amenities";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { Filters, PropertyType } from "@/types/filters";
import {
	ApolloLocation,
	enrichLocationsWithApolloData,
	enrichLocationsWithStoredDetails,
} from "../location-details-store";
import { decodeLocation } from "./encode-decode-location";
import { decodeMaxAgeAndPossession, decodeValue } from "./encode-decode-value";
import { propertyTypeMapValueFromLabel, subPropertyTypeValueFromLabel } from "./filters-to-url";
import { isKeyword } from "./is-keyword";
import { parseNumberFromUrl } from "./parse-number-from-url";

// Cache for performance optimization
const propertyTypeCache = new Map<string, PropertyType>();
const amenitiesCache = new Map<string, (typeof amenitiesInfo)[0][]>();

// Pre-populate property type cache
propertyTypeOptions.forEach(option => {
	if (option.value) {
		propertyTypeCache.set(option.label || "", option.value as PropertyType);
	}
});

export function urlToFilters(
	urlString: string,
	{ apolloLocations }: { apolloLocations?: ApolloLocation[] } = {},
): Partial<Filters> {
	// Use plain object instead of Object.create(null) for React serialization
	const filters: Partial<Filters> = {
		locations: [],
		documents: [],
		facing: [],
		spaces: [],
		waterSources: [],
		levelType: [],
		amenities: [],
	};

	if (!urlString) return filters;

	const segments = urlString.toLowerCase().split("-");
	const segmentCount = segments.length;
	let i = 0;

	let propertyType: PropertyType | undefined;
	let subTypeLabel: string | undefined;
	let isCommunity: boolean | undefined;
	const bhkSegments: string[] = [];
	while (i < segmentCount) {
		const segment = segments[i];
		if (!segment) {
			i++;
			continue;
		}

		// Optimized bedrooms parsing
		if (segment.includes("bhk")) {
			bhkSegments.push(segment);
			i++;
			continue;
		}

		// Optimized property type parsing with cache
		const cachedPropertyType = propertyTypeCache.get(segment);
		if (cachedPropertyType || ["apartments", "houses", "villas", "plots"].includes(segment)) {
			propertyType = cachedPropertyType || (propertyTypeMapValueFromLabel[segment] as PropertyType);
			if (propertyType) filters.propertyType = propertyType;
			i++;
			continue;
		}

		if (segment === "subtype" && propertyType) {
			i++;
			if (i < segmentCount) {
				subTypeLabel = segments[i];
			}
			i++;
			continue;
		}

		if (segment === "community" && propertyType && subTypeLabel) {
			i++;
			if (i < segmentCount) {
				isCommunity = segments[i] === "yes";
			}
			i++;
			continue;
		}

		// Optimized location parsing
		if (segment === "in") {
			const locationSegments: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				locationSegments.push(segments[i]);
				i++;
			}
			if (locationSegments.length > 0) {
				// Optimized location mapping with reduced object creation
				const locations = new Array(locationSegments.length);
				for (let j = 0; j < locationSegments.length; j++) {
					const segment = locationSegments[j];
					const locationName = decodeLocation(segment);
					const isProject = segment.endsWith("_project");
					locations[j] = {
						id: "",
						name: isProject ? locationName.replace(/project$/i, "").trim() : locationName,
						type: isProject ? "project" : ("landmark" as const),
					};
				}
				filters.locations = locations;
			}
			continue;
		}

		// Optimized facing parsing
		if (segment === "facing") {
			const facingValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				facingValues.push(segments[i]);
				i++;
			}
			if (facingValues.length > 0) {
				filters.facing = facingValues.map(decodeValue);
			}
			continue;
		}

		// Optimized plot facing parsing
		if (propertyType === "apartment" && segment === "plotfacing") {
			const plotFacingValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				plotFacingValues.push(segments[i]);
				i++;
			}
			if (plotFacingValues.length > 0) {
				filters.plotFacing = plotFacingValues.map(decodeValue);
			}
			continue;
		}

		// Optimized built-up area parsing
		if (segment === "builtuparea") {
			i++;
			if (i < segmentCount) {
				const areaValue = segments[i].replace("plus", "");
				const parsedArea = parseNumberFromUrl(areaValue);
				if (parsedArea > 0) {
					filters.builtupAreaMin = parsedArea;
				}
			}
			i++;
			continue;
		}

		// Optimized plot area parsing
		if (propertyType !== "apartment" && segment === "plotarea") {
			i++;
			if (i < segmentCount) {
				const areaValue = segments[i].replace("plus", "");
				const parsedArea = parseNumberFromUrl(areaValue);
				if (parsedArea > 0) {
					filters.plotAreaMin = parsedArea;
				}
			}
			i++;
			continue;
		}

		// Optimized budget parsing
		if (segment === "budget") {
			i++;
			let budgetMin = 0;
			let budgetVal = 0;
			let budgetMax = 0;

			while (i < segmentCount && !isKeyword(segments[i])) {
				const key = segments[i];
				const value = segments[i + 1];

				if (!value || isKeyword(value)) break;

				const parsed = parseNumberFromUrl(value);

				switch (key) {
					case "min":
						budgetMin = parsed;
						break;
					case "val":
						budgetVal = parsed;
						break;
					case "max":
						budgetMax = parsed;
						break;
				}

				i += 2;
			}

			if (budgetMin > 0 || budgetVal > 0 || budgetMax > 0) {
				filters.budget = {
					min: budgetMin,
					value: budgetVal,
					max: budgetMax,
				};
			}
			continue;
		}

		// Optimized bathrooms parsing with regex caching
		if (segment.includes("bathrooms")) {
			const match = segment.match(/(\d+)bathrooms/);
			if (match) {
				const count = parseInt(match[1], 10);
				if (count > 0) {
					filters.bathroomsMin = count;
				}
			}
			i++;
			continue;
		}

		// Optimized balconies parsing
		if (segment.includes("balconies")) {
			const match = segment.match(/(\d+)balconies/);
			if (match) {
				const count = parseInt(match[1], 10);
				if (count > 0) {
					filters.balconiesMin = count;
				}
			}
			i++;
			continue;
		}

		// Optimized parkings parsing
		if (segment.includes("parkings")) {
			const match = segment.match(/(\d+)parkings/);
			if (match) {
				const count = parseInt(match[1], 10);
				if (count > 0) {
					filters.parkingsMin = count;
				}
			}
			i++;
			continue;
		}

		// Optimized floors parsing
		if (segment === "floor" || segment === "floors") {
			i++;
			if (i < segmentCount) {
				const floorMin = parseInt(segments[i], 10);
				let floorMax = floorMin;

				if (i + 1 < segmentCount && !isKeyword(segments[i + 1])) {
					const nextFloor = parseInt(segments[i + 1], 10);
					if (!isNaN(nextFloor)) {
						floorMax = nextFloor;
						i++;
					}
				}

				if (!isNaN(floorMin)) {
					filters.floors = { min: floorMin, max: floorMax };
				}
			}
			i++;
			continue;
		}

		// Optimized status parsing
		if (segment === "status") {
			i++;
			if (i < segmentCount) {
				filters.status = decodeValue(segments[i]);
			}
			i++;
			continue;
		}

		// Optimized water sources parsing
		if (segment === "watersources") {
			const waterSourceValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				waterSourceValues.push(segments[i]);
				i++;
			}
			if (waterSourceValues.length > 0) {
				filters.waterSources = waterSourceValues.map(decodeValue);
			}
			continue;
		}

		// Optimized level type parsing
		if (segment === "level") {
			const levelValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				levelValues.push(segments[i]);
				i++;
			}
			if (levelValues.length > 0) {
				filters.levelType = levelValues.map(decodeValue);
			}
			continue;
		}

		// Optimized furnishing parsing
		if (segment === "furnishing") {
			i++;
			if (i < segmentCount) {
				filters.furnishing = decodeValue(segments[i]);
			}
			i++;
			continue;
		}

		// Optimized amenities parsing with caching
		if (segment === "amenities") {
			const amenityValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				amenityValues.push(segments[i]);
				i++;
			}

			if (amenityValues.length > 0) {
				const cacheKey = amenityValues.join(",");
				let cachedAmenities = amenitiesCache.get(cacheKey);

				if (!cachedAmenities) {
					const decodedAmenityValues = amenityValues.map(decodeValue);
					cachedAmenities = amenitiesInfo.filter(a => decodedAmenityValues.includes(a.code));
					amenitiesCache.set(cacheKey, cachedAmenities);
				}

				filters.amenities = cachedAmenities;
			}
			continue;
		}

		// Optimized documents parsing
		if (segment === "documents") {
			const documentValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				documentValues.push(segments[i]);
				i++;
			}
			if (documentValues.length > 0) {
				filters.documents = documentValues.map(decodeValue);
			}
			continue;
		}

		// Optimized spaces parsing
		if (segment === "spaces") {
			const spaceValues: string[] = [];
			i++;
			while (i < segmentCount && !isKeyword(segments[i])) {
				let space = segments[i];
				// Convert "space_bathrooms" to "bathrooms" for filters
				if (space === "space_bathrooms") space = "bathrooms";
				spaceValues.push(space);
				i++;
			}
			if (spaceValues.length > 0) {
				filters.spaces = spaceValues.map(decodeValue);
			}
			continue;
		}

		// Optimized rental value parsing
		if (segment === "rentalvalue") {
			i++;
			if (i < segmentCount) {
				const rentalMin = parseNumberFromUrl(segments[i]);
				let rentalMax = rentalMin;

				if (i + 1 < segmentCount && !isKeyword(segments[i + 1])) {
					const nextRental = parseNumberFromUrl(segments[i + 1]);
					if (!isNaN(nextRental)) {
						rentalMax = nextRental;
						i++;
					}
				}

				if (!isNaN(rentalMin)) {
					filters.rentalValue = { min: rentalMin, max: rentalMax };
				}
			}
			i++;
			continue;
		}

		// Optimized maxAge parsing
		if (segment === "maxage") {
			i++;
			if (i < segmentCount) {
				filters.maxAge = decodeMaxAgeAndPossession(segments[i]);
			}
			i++;
			continue;
		}

		// Optimized possession parsing
		if (segment === "possession" && i + 1 < segmentCount && segments[i + 1] !== "ready") {
			i++;
			if (i < segmentCount) {
				filters.possession = decodeMaxAgeAndPossession(segments[i]);
			}
			i++;
			continue;
		}

		// Optimized zoom parsing - now handled as search parameter
		// if (segment === "zoom") {
		//	i++;
		//	if (i < segmentCount) {
		//		const zoom = parseInt(segments[i], 10);
		//		if (!isNaN(zoom) && zoom > 0) {
		//			filters.zoom = zoom;
		//		}
		//	}
		//	i++;
		//	continue;
		// }

		// Optimized coordinates parsing - now handled as search parameters
		// if (segment === "lat") {
		//	i++;
		//	if (i < segmentCount) {
		//		const lat = parseFloat(segments[i]);
		//		if (!isNaN(lat) && lat >= -90 && lat <= 90) {
		//			if (!filters.coordinates) filters.coordinates = { lat: 0, lng: 0 };
		//			filters.coordinates.lat = lat;
		//		}
		//	}
		//	i++;
		//	continue;
		// }

		// if (segment === "lng") {
		//	i++;
		//	if (i < segmentCount) {
		//		const lng = parseFloat(segments[i]);
		//		if (!isNaN(lng) && lng >= -180 && lng <= 180) {
		//			if (!filters.coordinates) filters.coordinates = { lat: 0, lng: 0 };
		//			filters.coordinates.lng = lng;
		//		}
		//	}
		//	i++;
		//	continue;
		// }

		// Move to next segment if no specific parsing matched
		i++;
	}

	// Optimized BHK processing with reduced allocations
	if (bhkSegments.length > 0) {
		const bedroomValues: number[] = [];

		for (const seg of bhkSegments) {
			const match = seg.match(/^(\d+(?:\.\d+)?)bhk$/);
			if (match) {
				const value = parseFloat(match[1]);
				if (!isNaN(value) && value > 0) {
					bedroomValues.push(value);
				}
			}
		}

		if (bedroomValues.length > 0) {
			const min = Math.min(...bedroomValues);
			const max = Math.max(...bedroomValues);
			filters.bedrooms = { min, max: max || min };
		}
	}

	// Optimized property type processing
	if (propertyType) {
		let subTypeValue: string | undefined;
		if (subTypeLabel) {
			subTypeValue = subPropertyTypeValueFromLabel[propertyType]?.[subTypeLabel];
		}

		if (!subTypeValue) {
			const propertyOption = propertyTypeOptions.find(type => type.value === propertyType);
			subTypeValue = propertyOption?.options?.[0]?.value;
		}

		if (subTypeValue) {
			filters.subType = subTypeValue;

			if (isCommunity) filters.isCommunity = true;
		}
	}

	// Optimized location enrichment with early return
	if (filters.locations?.length) {
		// Use mapData if available (SSR), otherwise fall back to localStorage (client-side)
		if (apolloLocations?.length) {
			filters.locations = enrichLocationsWithApolloData({ locations: filters.locations, apolloLocations });
		} else if (typeof window !== "undefined") {
			// Only use localStorage on client-side
			filters.locations = enrichLocationsWithStoredDetails(filters.locations);
		}
	}

	return filters;
}
